/**
 * PA Queue - Production Art Department View
 *
 * Design: Compact list view for holistic visibility
 * - See many projects at once
 * - Key metrics: Jobs, Progress, Current Step
 * - Click to expand job/SKU details
 * - Grouped by PA status based on routing operations:
 *   - Waiting on Files: No jobs loaded
 *   - Queued: Has jobs, PROOF-PDF not started
 *   - Proofing: PROOF-PDF in progress
 *   - Awaiting Approval: PROOF-PDF done, PDF APPROVAL pending
 *   - Printing: PDF APPROVAL done, PRINTS-DONE pending
 *   - Complete: PRINTS-DONE complete
 *   - On Hold: Flagged in SharePoint
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useReworkQueue } from "../../hooks/useReworkQueue";
import ReworkQueueItem from "../ReworkQueueItem";
import ReworkWarningBadge from "../ReworkWarningBadge";
import { ProgressDots, STAGE_LABELS } from "../shared/ProgressDots";

const API_BASE = import.meta.env.VITE_API_URL || "";

// PA Bucket configuration - Simplified workflow stages
const PA_BUCKETS = {
  coming_soon: { label: "Coming Soon", color: "gray", order: 0 },
  new_ready: { label: "NEW, Ready to Work", color: "blue", order: 1 },
  proofing: { label: "Proofing in Progress", color: "yellow", order: 2 },
  awaiting_approval: { label: "Awaiting Client Approval", color: "amber", order: 3 },
  approved_printing: { label: "Approved / Printing", color: "purple", order: 4 },
  finals_printed: { label: "Finals Printed", color: "green", order: 5, collapsed: true },
  on_hold: { label: "On Hold", color: "red", order: 99, collapsed: true },
};

// Map backend bucket names to new frontend buckets
const BUCKET_MAP = {
  // Coming Soon - no files ready, not started
  'queued': 'coming_soon',
  // NEW, Ready to Work - unassigned or ready to proof
  'unassigned': 'new_ready',
  'ready_to_proof': 'new_ready',
  // Proofing in Progress
  'proofing': 'proofing',
  // Awaiting Client Approval
  'waiting_for_approval': 'awaiting_approval',
  // Approved / Printing
  'approved_ready_to_print': 'approved_printing',
  'printing': 'approved_printing',
  // Finals Printed
  'pa_complete': 'finals_printed',
  // On Hold
  'on_hold': 'on_hold',
};

// Mock printers (TODO: integrate with real printer status API)
const MOCK_PRINTERS = [
  { id: "1", name: "S80600-1", status: "idle" },
  { id: "2", name: "S80600-2", status: "printing", job: "WO-2025-0130" },
  { id: "3", name: "LEF-300", status: "idle" },
  { id: "4", name: "LEF-200", status: "maintenance" },
  { id: "5", name: "VO-1", status: "idle" },
];

export default function PAQueue({ employeeId, displayName, token, onSelectProject }) {
  // Bucket state - simplified workflow stages
  const [buckets, setBuckets] = useState({
    coming_soon: [],
    new_ready: [],
    proofing: [],
    awaiting_approval: [],
    approved_printing: [],
    finals_printed: [],
    on_hold: [],
  });
  const [bucketCounts, setBucketCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Selected project for right panel detail view
  const [selectedProject, setSelectedProject] = useState(null);

  // Lazy loading for jobs - only fetch when selecting a project
  const [loadingJobsFor, setLoadingJobsFor] = useState(null); // ID of project currently loading jobs
  const [loadedJobs, setLoadedJobs] = useState({}); // { [projectId]: jobs[] }

  // Data freshness
  const [lastSync, setLastSync] = useState(null);
  const [dataSource, setDataSource] = useState(null);

  // Notification modal state
  const [showFinalsModal, setShowFinalsModal] = useState(false);
  const [finalsWorkOrder, setFinalsWorkOrder] = useState(null);
  const [rackLocation, setRackLocation] = useState("");
  const [finalsNotes, setFinalsNotes] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);

  // Rework queue - items that need rework at PA
  const {
    queueItems: reworkItems,
    warnings: reworkWarnings,
    loading: reworkLoading,
    scrapCount,
    totalCount: reworkCount,
    refetch: refetchRework,
    getWarningsForJob,
    hasRework,
  } = useReworkQueue('PA', { refreshInterval: 60000 });

  // Fetch PA Queue data from unified job_stages table (same source as All Projects and Assembly)
  // Note: Jobs are loaded lazily when expanding a project (for faster initial load)
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      // Use unified PA endpoint for consistent data with All Projects and Assembly views
      const response = await fetch(`${API_BASE}/api/unified/pa/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.status}`);
      }

      const data = await response.json();

      // Transform unified API response to component format
      const transform = (project) => ({
        id: project.id || project.quoteNumber,
        woNumber: project.orderNumber || `Q-${project.quoteNumber}`,
        quoteNumber: project.quoteNumber,
        projectName: project.projectName,
        pm: project.pm,
        assignedTo: project.artist,
        assignedArtistId: project.artist ? 'assigned' : null, // Simplified - check if has artist name
        paStatus: project.paStatus || 'queued',
        currentStep: project.worstStage, // Use canonical stage as current step
        progress: { completed: 0, total: 0, percent: 0 }, // Derived from stage
        isRush: project.atRisk,
        proofDue: null, // Not available from job_stages
        proofSentAt: null,
        proofApprovedAt: null,
        approvedAgo: null,
        assemblyNeeds: null,
        // Jobs/SKUs from job_stages
        jobs: project.jobs || [],
        skus: project.skus || [],
        jobCount: project.jobCount || 0,
        // Quantities
        totalComps: project.totalQty || 0,
        poaCount: null, // Not tracked in job_stages
        // Extra fields
        shipDate: project.shipDate,
        status: project.onHold ? 'on_hold' : 'active',
        trackerStatus: null,
        customerName: project.customerName,
        salesRep: null,
        orders: [],
        orderCount: 0,
        // New fields from unified endpoint
        worstStage: project.worstStage,
        lastUpdated: project.lastUpdated,
      });

      // Group projects by PA status
      const projects = (data.projects || []).map(transform);

      // Map backend status to frontend bucket
      const mapToBucket = (status) => BUCKET_MAP[status] || 'coming_soon';

      // Group by mapped bucket
      const grouped = {
        coming_soon: projects.filter(p => mapToBucket(p.paStatus) === 'coming_soon'),
        new_ready: projects.filter(p => mapToBucket(p.paStatus) === 'new_ready'),
        proofing: projects.filter(p => mapToBucket(p.paStatus) === 'proofing'),
        awaiting_approval: projects.filter(p => mapToBucket(p.paStatus) === 'awaiting_approval'),
        approved_printing: projects.filter(p => mapToBucket(p.paStatus) === 'approved_printing'),
        finals_printed: projects.filter(p => mapToBucket(p.paStatus) === 'finals_printed').slice(0, 20),
        on_hold: projects.filter(p => mapToBucket(p.paStatus) === 'on_hold'),
      };

      setBuckets(grouped);
      setBucketCounts(data.buckets || {});

      // Store data freshness info
      setLastSync(data.lastSync ? new Date(data.lastSync) : null);
      setDataSource(data.source || 'unknown');

      // Log data source for debugging
      console.log(`PA Queue: Loaded ${projects.length} projects from ${data.source}, last sync: ${data.lastSync}`);
    } catch (err) {
      console.error("Error fetching PA queue:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Lazy load jobs for a project
  const fetchJobsForProject = useCallback(async (quoteNumber) => {
    if (!quoteNumber || loadedJobs[quoteNumber]) return;

    setLoadingJobsFor(quoteNumber);
    try {
      const response = await fetch(`${API_BASE}/api/v2/production/projects/${encodeURIComponent(quoteNumber)}/jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setLoadedJobs(prev => ({ ...prev, [quoteNumber]: data.jobs || [] }));
      }
    } catch (err) {
      console.error(`Error fetching jobs for ${quoteNumber}:`, err);
    } finally {
      setLoadingJobsFor(null);
    }
  }, [token, loadedJobs]);

  const toggleExpand = (id, quoteNumber) => {
    const newExpandedId = expandedId === id ? null : id;
    setExpandedId(newExpandedId);
    // Lazy load jobs when expanding (if not already loaded)
    if (newExpandedId && quoteNumber && !loadedJobs[quoteNumber]) {
      fetchJobsForProject(quoteNumber);
    }
  };

  // Select a project to show in the right panel
  const handleSelectProjectForPanel = (project) => {
    const isSameProject = selectedProject?.id === project.id;
    if (isSameProject) {
      setSelectedProject(null); // Deselect if clicking same project
    } else {
      setSelectedProject(project);
      // Load jobs for the selected project
      if (project.quoteNumber && !loadedJobs[project.quoteNumber]) {
        fetchJobsForProject(project.quoteNumber);
      }
    }
  };

  // Display all buckets (team view)
  const displayBuckets = buckets;

  // Compute total count
  const totalCount = useMemo(() => {
    return Object.values(buckets).reduce((sum, items) => sum + items.length, 0);
  }, [buckets]);

  // Action handlers
  const handlePickUp = async (workOrderId) => {
    try {
      const response = await fetch(`${API_BASE}/api/pa-queue/${workOrderId}/pick-up`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to pick up work order");
      }

      // Refresh data
      fetchData();
    } catch (err) {
      console.error("Error picking up work order:", err);
      alert("Failed to pick up work order");
    }
  };

  const handleMarkProofSent = async (workOrderId) => {
    try {
      const response = await fetch(`${API_BASE}/api/pa-queue/${workOrderId}/proof-sent`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to mark proof sent");
      }

      fetchData();
    } catch (err) {
      console.error("Error marking proof sent:", err);
      alert("Failed to mark proof sent");
    }
  };

  const handleMarkApproved = async (quoteNumber) => {
    try {
      // Use unified PA endpoint which works with quote numbers (project IDs)
      const response = await fetch(`${API_BASE}/api/unified/pa/project/${encodeURIComponent(quoteNumber)}/proof-approved`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to mark proof approved");
      }

      fetchData();
    } catch (err) {
      console.error("Error marking proof approved:", err);
      alert("Failed to mark proof approved");
    }
  };

  // Open "Send to Assembly" modal
  const handleOpenFinalsModal = (workOrder) => {
    setFinalsWorkOrder(workOrder);
    setRackLocation("");
    setFinalsNotes("");
    setShowFinalsModal(true);
  };

  // Send "Finals Ready" notification to Assembly
  const handleSendFinalsNotification = async () => {
    if (!finalsWorkOrder || !rackLocation.trim()) return;

    setSendingNotification(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/pa-queue/${finalsWorkOrder.id}/notify/finals-ready`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            rackLocation: rackLocation.trim(),
            notes: finalsNotes.trim() || null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send notification");
      }

      // Close modal and refresh
      setShowFinalsModal(false);
      setFinalsWorkOrder(null);
      fetchData();
    } catch (err) {
      console.error("Error sending finals notification:", err);
      alert("Failed to send notification to Assembly");
    } finally {
      setSendingNotification(false);
    }
  };

  // Send "PDFs Ready" notification to PM
  const handleNotifyPdfsReady = async (workOrderId, concerns = "") => {
    try {
      const response = await fetch(
        `${API_BASE}/api/pa-queue/${workOrderId}/notify/pdfs-ready`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ concerns }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send notification");
      }

      // Also mark proof as sent
      await handleMarkProofSent(workOrderId);
    } catch (err) {
      console.error("Error sending PDFs ready notification:", err);
      alert("Failed to notify PM");
    }
  };

  if (loading) {
    return (
      <div className="flex-1 bg-slate-200 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading PA Queue...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 bg-slate-200 text-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">Error: {error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-200 text-gray-900 overflow-auto">
      {/* Header - FULL WIDTH */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Production Art</h1>
            <p className="text-sm text-gray-500">
              {totalCount} projects
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Data freshness indicator */}
            {lastSync && (
              <span className="text-xs text-gray-400" title={`Data source: ${dataSource}`}>
                Synced {lastSync.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* REWORK REQUIRED - Priority section at top */}
      {reworkCount > 0 && (
        <div className="px-4 pb-4">
          <section className="bg-white rounded-lg border border-red-200 p-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider mb-3">
              <span className="text-red-500">🔧</span>
              <span className="text-red-700">Rework Required</span>
              <span className="ml-auto px-2 py-0.5 bg-red-100 rounded-full text-xs font-medium text-red-700">
                {reworkCount}
              </span>
              {scrapCount > 0 && (
                <span className="px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">
                  {scrapCount} SCRAP
                </span>
              )}
            </div>
            <div className="space-y-2">
              {reworkItems.map((item) => (
                <ReworkQueueItem
                  key={item.id}
                  item={item}
                  onStart={() => {
                    // TODO: Navigate to start rework for this job
                    console.log('Start rework:', item);
                  }}
                  onView={() => {
                    // TODO: Open rework detail modal
                    console.log('View rework:', item);
                  }}
                />
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Kanban Overview - FULL WIDTH */}
      <div className="px-4 pb-4">
        <KanbanOverview buckets={displayBuckets} onSelectProject={handleSelectProjectForPanel} />
      </div>

      {/* Sections - constrained width */}
      <div className="max-w-7xl mx-auto px-4 pb-4">
        {/* Two-column layout: Queue list (60%) + Right panel (40%) */}
        <div className="flex gap-4">
          {/* Main Queue List - 60% width */}
          <div className="w-[60%] space-y-4">
            {/* NEW, Ready to Work */}
            <Section title={PA_BUCKETS.new_ready.label} count={displayBuckets.new_ready?.length || 0} color={PA_BUCKETS.new_ready.color}>
              {(displayBuckets.new_ready?.length || 0) === 0 ? (
                <EmptyRow>No new projects ready to work</EmptyRow>
              ) : (
                <ProjectList
                  projects={displayBuckets.new_ready}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                  onPickUp={handlePickUp}
                  currentUserId={employeeId}
                  getWarningsForJob={getWarningsForJob}
                />
              )}
            </Section>

            {/* Proofing in Progress */}
            <Section title={PA_BUCKETS.proofing.label} count={displayBuckets.proofing?.length || 0} color={PA_BUCKETS.proofing.color}>
              {(displayBuckets.proofing?.length || 0) === 0 ? (
                <EmptyRow>No proofs in progress</EmptyRow>
              ) : (
                <ProjectList
                  projects={displayBuckets.proofing}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                  onProofSent={handleMarkProofSent}
                  onNotifyPM={handleNotifyPdfsReady}
                  currentUserId={employeeId}
                  getWarningsForJob={getWarningsForJob}
                />
              )}
            </Section>

            {/* Awaiting Client Approval */}
            <Section title={PA_BUCKETS.awaiting_approval.label} count={displayBuckets.awaiting_approval?.length || 0} color={PA_BUCKETS.awaiting_approval.color}>
              {(displayBuckets.awaiting_approval?.length || 0) === 0 ? (
                <EmptyRow>No proofs awaiting approval</EmptyRow>
              ) : (
                <ProjectList
                  projects={displayBuckets.awaiting_approval}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                  onApproved={handleMarkApproved}
                  getWarningsForJob={getWarningsForJob}
                />
              )}
            </Section>

            {/* Approved / Printing */}
            <Section title={PA_BUCKETS.approved_printing.label} count={displayBuckets.approved_printing?.length || 0} color={PA_BUCKETS.approved_printing.color}>
              {(displayBuckets.approved_printing?.length || 0) === 0 ? (
                <EmptyRow>No jobs approved or printing</EmptyRow>
              ) : (
                <ProjectList
                  projects={displayBuckets.approved_printing}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                  onSendToAssembly={handleOpenFinalsModal}
                  getWarningsForJob={getWarningsForJob}
                />
              )}
            </Section>

            {/* On Hold (collapsed by default) */}
            {(displayBuckets.on_hold?.length || 0) > 0 && (
              <Section title={PA_BUCKETS.on_hold.label} count={displayBuckets.on_hold.length} color={PA_BUCKETS.on_hold.color} collapsed>
                <ProjectList
                  projects={displayBuckets.on_hold}
                  selectedId={selectedProject?.id}
                  onSelect={handleSelectProjectForPanel}
                  onViewDetail={onSelectProject}
                  getWarningsForJob={getWarningsForJob}
                />
              </Section>
            )}
          </div>

          {/* Right Panel - 40% width - SKU Detail View */}
          <div className="w-[40%] flex-shrink-0">
            <div className="sticky top-24">
              <SKUDetailPanel
                project={selectedProject}
                jobs={selectedProject ? (loadedJobs[selectedProject.quoteNumber] || selectedProject.jobs || []) : []}
                loading={loadingJobsFor === selectedProject?.quoteNumber}
                onViewDetail={onSelectProject}
                onPickUp={handlePickUp}
                onNotifyPM={handleNotifyPdfsReady}
                onApproved={handleMarkApproved}
                onSendToAssembly={handleOpenFinalsModal}
                onClose={() => setSelectedProject(null)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Send to Assembly Modal */}
      {showFinalsModal && finalsWorkOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowFinalsModal(false)}
          />
          <div className="relative bg-white border border-gray-200 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Send to Assembly</h2>
              <p className="text-xs text-gray-500 mt-1">
                Notify 2D Assembly that prints are ready for {finalsWorkOrder.projectName}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rack Location <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={rackLocation}
                  onChange={(e) => setRackLocation(e.target.value)}
                  placeholder="e.g., Rack A3, Shelf 2"
                  className="w-full px-4 py-2.5 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes for Assembly <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <textarea
                  value={finalsNotes}
                  onChange={(e) => setFinalsNotes(e.target.value)}
                  placeholder="Any special instructions..."
                  rows={3}
                  className="w-full px-4 py-2.5 bg-slate-100 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-green-500 text-sm resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowFinalsModal(false)}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-100 text-gray-700 rounded-lg transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSendFinalsNotification}
                disabled={!rackLocation.trim() || sendingNotification}
                className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:text-gray-500 text-gray-900 rounded-lg transition-colors text-sm font-medium"
              >
                {sendingNotification ? "Sending..." : "Send to Assembly"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   HELPER FUNCTIONS
───────────────────────────────────────────────────────────────────────────── */

function formatProofDue(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "Overdue";
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays <= 7) {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours}h`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatAssemblyNeeds(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return date.toLocaleDateString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });
}

function formatShipDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  // Always show actual date in short format (e.g., "Jan 15" or "Mon, Jan 15")
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // Show weekday for dates within 7 days
  if (diffDays >= 0 && diffDays <= 7) {
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Compare dates only
  return date < now;
}

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────────────────────────────────────────── */

function Section({ title, count, color, children, collapsed: defaultCollapsed = true }) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const colorClasses = {
    gray: { dot: "bg-gray-400", border: "border-gray-300", bg: "bg-gray-50", header: "bg-gray-100", text: "text-gray-700" },
    blue: { dot: "bg-blue-500", border: "border-blue-300", bg: "bg-blue-50/50", header: "bg-blue-100", text: "text-blue-700" },
    yellow: { dot: "bg-yellow-500", border: "border-yellow-300", bg: "bg-yellow-50/50", header: "bg-yellow-100", text: "text-yellow-700" },
    amber: { dot: "bg-amber-500", border: "border-amber-300", bg: "bg-amber-50/50", header: "bg-amber-100", text: "text-amber-700" },
    orange: { dot: "bg-orange-500", border: "border-orange-300", bg: "bg-orange-50/50", header: "bg-orange-100", text: "text-orange-700" },
    green: { dot: "bg-green-500", border: "border-green-300", bg: "bg-green-50/50", header: "bg-green-100", text: "text-green-700" },
    purple: { dot: "bg-purple-500", border: "border-purple-300", bg: "bg-purple-50/50", header: "bg-purple-100", text: "text-purple-700" },
    indigo: { dot: "bg-indigo-500", border: "border-indigo-300", bg: "bg-indigo-50/50", header: "bg-indigo-100", text: "text-indigo-700" },
    red: { dot: "bg-red-500", border: "border-red-300", bg: "bg-red-50/50", header: "bg-red-100", text: "text-red-700" },
  };

  const colors = colorClasses[color] || colorClasses.gray;

  return (
    <section className="mb-4">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className={`
          w-full flex items-center gap-2 text-xs font-semibold uppercase tracking-wider
          px-3 py-2 rounded-lg cursor-pointer transition-all
          border-l-4 ${colors.border} ${colors.bg}
          hover:shadow-sm
        `}
      >
        <svg
          className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className={`w-2 h-2 rounded-full ${colors.dot}`} />
        <span className={colors.text}>{title}</span>
        <span className={`ml-auto px-2 py-0.5 ${colors.header} rounded-full text-xs font-medium ${colors.text}`}>
          {count}
        </span>
      </button>
      {!isCollapsed && (
        <div className="mt-2 bg-white rounded-lg border border-gray-200 overflow-hidden">
          {children}
        </div>
      )}
    </section>
  );
}

function ProjectList({
  projects,
  selectedId,
  onSelect,
  onViewDetail,
  onPickUp,
  onProofSent,
  onApproved,
  onNotifyPM,
  onSendToAssembly,
  currentUserId,
  getWarningsForJob,
}) {
  return (
    <div className="divide-y divide-gray-200/50">
      {/* Header row */}
      <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs text-gray-500 font-medium bg-white/80 border-b border-gray-100">
        <div className="col-span-1">Quote</div>
        <div className="col-span-5">Project Name</div>
        <div className="col-span-2 text-center">Artist</div>
        <div className="col-span-1 text-center">SKUs</div>
        <div className="col-span-1 text-center">Qty</div>
        <div className="col-span-2 text-right">Ship Date</div>
      </div>
      {/* Project rows */}
      {projects.map((wo) => (
        <ProjectRow
          key={wo.id}
          wo={wo}
          isSelected={selectedId === wo.id}
          onSelect={() => onSelect(wo)}
          getWarningsForJob={getWarningsForJob}
        />
      ))}
    </div>
  );
}

function ProjectRow({
  wo,
  isSelected,
  onSelect,
  getWarningsForJob,
}) {
  const jobs = wo.jobs || [];

  // Get warnings for all jobs in this project
  const projectWarnings = useMemo(() => {
    if (!getWarningsForJob || !jobs.length) return [];
    return jobs.flatMap(job => getWarningsForJob(job.jobNumber) || []);
  }, [jobs, getWarningsForJob]);

  return (
    <div
      onClick={onSelect}
      className={`grid grid-cols-12 gap-2 px-4 py-2.5 items-center cursor-pointer transition-colors ${
        isSelected
          ? "bg-purple-50 border-l-4 border-purple-500"
          : "hover:bg-gray-50 border-l-4 border-transparent"
      }`}
    >
      {/* Quote Number + Rush indicator + Rework warnings */}
      <div className="col-span-1 flex items-center gap-1">
        {wo.isRush && (
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 animate-pulse" title="Rush" />
        )}
        <span className="font-mono text-xs text-purple-600 font-medium">
          {wo.quoteNumber || "—"}
        </span>
        {projectWarnings.length > 0 && (
          <ReworkWarningBadge warnings={projectWarnings} compact />
        )}
      </div>

      {/* Project Name */}
      <div className="col-span-5">
        <span className="text-sm text-gray-900 truncate block font-medium">{wo.projectName}</span>
        {wo.customerName && (
          <span className="text-xs text-gray-500 truncate block">{wo.customerName}</span>
        )}
      </div>

      {/* Assigned Artist */}
      <div className="col-span-2 text-center">
        {wo.assignedTo ? (
          <span className="text-xs text-gray-700 font-medium">{wo.assignedTo}</span>
        ) : (
          <span className="text-xs text-orange-500 font-medium">Unassigned</span>
        )}
      </div>

      {/* Job Count */}
      <div className="col-span-1 text-center">
        <span className="text-sm text-blue-600 font-semibold">{wo.jobCount || 0}</span>
      </div>

      {/* Total Comps */}
      <div className="col-span-1 text-center">
        {wo.totalComps > 0 ? (
          <span className="text-sm text-indigo-600 font-semibold">{wo.totalComps}</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </div>

      {/* Ship Date */}
      <div className="col-span-2 text-right">
        {wo.shipDate ? (
          <span className={`text-xs font-medium ${isOverdue(wo.shipDate) ? 'text-red-600' : 'text-gray-600'}`}>
            {formatShipDate(wo.shipDate)}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </div>
    </div>
  );
}

function ProgressBadge({ progress }) {
  if (!progress || progress.total === 0) {
    return <span className="text-xs text-gray-500">—</span>;
  }

  const { completed, total, percent } = progress;
  const color = percent >= 75 ? "green" : percent >= 50 ? "amber" : percent >= 25 ? "blue" : "gray";
  const colors = {
    green: "bg-green-500/20 text-green-400",
    amber: "bg-amber-500/20 text-amber-400",
    blue: "bg-blue-500/20 text-blue-400",
    gray: "bg-slate-200/20 text-gray-500",
  }[color];

  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded ${colors}`}>
      {completed}/{total}
    </span>
  );
}

function PrintWindowBadge({ days }) {
  if (days === null || days === undefined) {
    return <span className="text-xs text-gray-500">—</span>;
  }
  const color = days >= 3 ? "green" : days >= 2 ? "amber" : "red";
  const colors = {
    green: "bg-green-500/20 text-green-400 border-green-500/30",
    amber: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    red: "bg-red-500/20 text-red-400 border-red-500/30",
  }[color];

  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded border ${colors}`}>{days}d</span>
  );
}

function PrinterRow({ printer }) {
  const config = {
    idle: { dot: "bg-green-500", text: "text-green-400", label: "Idle" },
    printing: { dot: "bg-blue-500 animate-pulse", text: "text-blue-400", label: "Printing" },
    maintenance: { dot: "bg-amber-500", text: "text-amber-400", label: "Maint" },
  }[printer.status];

  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs text-gray-700">{printer.name}</span>
      <span className={`flex items-center gap-1.5 text-xs ${config.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
        {config.label}
      </span>
    </div>
  );
}

function EmptyRow({ children }) {
  return <div className="px-4 py-6 text-center text-gray-600 text-sm">{children}</div>;
}

/* ─────────────────────────────────────────────────────────────────────────────
   KANBAN OVERVIEW
───────────────────────────────────────────────────────────────────────────── */

function KanbanOverview({ buckets, onSelectProject }) {
  // Main active work buckets
  const bucketOrder = ['new_ready', 'proofing', 'awaiting_approval', 'approved_printing'];
  // Secondary buckets (shown as compact summaries)
  const secondaryBuckets = ['coming_soon', 'finals_printed', 'on_hold'];

  // Calculate totals for secondary buckets
  const getSecondaryTotals = (bucketKey) => {
    const projects = buckets[bucketKey] || [];
    return {
      projects: projects.length,
      skus: projects.reduce((sum, p) => sum + (p.jobCount || 0), 0),
      comps: projects.reduce((sum, p) => sum + (p.totalComps || 0), 0),
    };
  };

  return (
    <div className="mb-4 bg-white rounded-lg border border-gray-200 p-4">
      {/* Workflow label */}
      <div className="flex items-center gap-2 mb-3 text-xs text-gray-400">
        <span>Workflow</span>
        <div className="flex-1 h-px bg-gradient-to-r from-gray-200 to-transparent" />
      </div>

      <div className="flex items-stretch">
        {bucketOrder.map((bucketKey, index) => {
          const config = PA_BUCKETS[bucketKey];
          const projects = buckets[bucketKey] || [];
          const isLast = index === bucketOrder.length - 1;

          return (
            <div key={bucketKey} className="flex items-stretch flex-1 min-w-0">
              <KanbanColumn
                title={config.label}
                color={config.color}
                projects={projects}
                onSelectProject={onSelectProject}
                stepNumber={index + 1}
              />
              {/* Arrow between columns */}
              {!isLast && (
                <div className="flex items-center px-1 text-gray-300">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Secondary buckets - compact summary cards */}
      <div className="flex gap-3 mt-3 pt-3 border-t border-gray-100">
        {secondaryBuckets.map((bucketKey) => {
          const config = PA_BUCKETS[bucketKey];
          const totals = getSecondaryTotals(bucketKey);
          if (totals.projects === 0) return null;

          return (
            <SummaryCard
              key={bucketKey}
              label={config.label}
              color={config.color}
              totals={totals}
            />
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ label, color, totals }) {
  const colorClasses = {
    gray: 'bg-gray-50 border-gray-200 text-gray-600',
    red: 'bg-red-50 border-red-200 text-red-600',
    green: 'bg-green-50 border-green-200 text-green-600',
  };
  const colors = colorClasses[color] || colorClasses.gray;

  return (
    <div className={`px-3 py-2 rounded-lg border ${colors} text-xs`}>
      <div className="font-semibold mb-1">{label}</div>
      <div className="flex gap-3 text-[10px]">
        <span><strong>{totals.projects}</strong> projects</span>
        <span><strong>{totals.skus}</strong> SKUs</span>
        <span><strong>{totals.comps}</strong> comps</span>
      </div>
    </div>
  );
}

function KanbanColumn({ title, color, projects, onSelectProject, stepNumber }) {
  const colorClasses = {
    gray: { header: 'bg-gray-100 text-gray-700 border-gray-200', body: 'bg-gray-50', accent: 'bg-gray-400' },
    blue: { header: 'bg-blue-100 text-blue-800 border-blue-200', body: 'bg-blue-50/30', accent: 'bg-blue-500' },
    yellow: { header: 'bg-yellow-100 text-yellow-800 border-yellow-200', body: 'bg-yellow-50/30', accent: 'bg-yellow-500' },
    amber: { header: 'bg-amber-100 text-amber-800 border-amber-200', body: 'bg-amber-50/30', accent: 'bg-amber-500' },
    purple: { header: 'bg-purple-100 text-purple-800 border-purple-200', body: 'bg-purple-50/30', accent: 'bg-purple-500' },
    green: { header: 'bg-green-100 text-green-800 border-green-200', body: 'bg-green-50/30', accent: 'bg-green-500' },
  };

  const colors = colorClasses[color] || colorClasses.gray;

  // Calculate bucket totals
  const totalSKUs = projects.reduce((sum, p) => sum + (p.jobCount || 0), 0);
  const totalComps = projects.reduce((sum, p) => sum + (p.totalComps || 0), 0);

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      {/* Column header */}
      <div className={`px-3 py-2 rounded-t-lg border ${colors.header} ${colors.body}`}>
        <div className="flex items-center gap-2">
          <span className={`w-5 h-5 rounded-full ${colors.accent} text-white text-[10px] font-bold flex items-center justify-center`}>
            {stepNumber}
          </span>
          <span className="text-sm font-bold truncate">{title}</span>
        </div>
        <div className="text-xs font-medium mt-1 opacity-70">{projects.length} project{projects.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Column body - compact scrollable list */}
      <div className={`flex-1 border border-t-0 border-gray-200 px-1.5 py-1.5 ${colors.body} max-h-[280px] overflow-y-auto`}>
        {/* Column headers for SKUs/Comps */}
        <div className="flex items-center gap-1.5 px-1.5 py-0.5 text-[9px] text-gray-400 font-medium border-b border-gray-200 mb-1">
          <span className="w-10 flex-shrink-0">Quote</span>
          <span className="flex-1 truncate">Project</span>
          <span className="w-6 text-right">SKU</span>
          <span className="w-8 text-right">Qty</span>
        </div>
        {projects.length === 0 ? (
          <div className="text-xs text-gray-400 text-center py-3 italic">No projects</div>
        ) : (
          <div className="space-y-0.5">
            {projects.map(p => (
              <KanbanRow key={p.id} project={p} onSelect={onSelectProject} />
            ))}
          </div>
        )}
      </div>

      {/* Column footer - totals */}
      <div className={`border border-t-0 border-gray-200 rounded-b-lg px-1.5 py-1.5 ${colors.body}`}>
        <div className="flex items-center gap-1.5 px-1.5 py-0.5 text-[10px] font-semibold border-t border-gray-300 pt-1">
          <span className="flex-1 text-gray-600">Total:</span>
          <span className="w-6 text-right text-blue-700">{totalSKUs}</span>
          <span className="w-8 text-right text-indigo-700">{totalComps}</span>
        </div>
      </div>
    </div>
  );
}

function KanbanRow({ project, onSelect }) {
  return (
    <div
      onClick={() => onSelect?.(project)}
      className="flex items-center gap-1.5 px-1.5 py-0.5 rounded cursor-pointer hover:bg-white transition-colors text-[10px]"
    >
      <span className="font-mono text-purple-600 font-medium w-10 flex-shrink-0">{project.quoteNumber}</span>
      <span className="text-gray-700 truncate flex-1 font-medium">{project.projectName}</span>
      <span className="text-blue-600 font-semibold w-6 text-right" title="SKUs">{project.jobCount || 0}</span>
      <span className="text-indigo-600 font-semibold w-8 text-right" title="Comps">{project.totalComps || 0}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SKU DETAIL PANEL - Right side panel showing selected project's SKUs
───────────────────────────────────────────────────────────────────────────── */

function SKUDetailPanel({
  project,
  jobs,
  loading,
  onViewDetail,
  onPickUp,
  onNotifyPM,
  onApproved,
  onSendToAssembly,
  onClose,
}) {
  if (!project) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 border-dashed p-8 text-center">
        <div className="text-gray-400 mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <p className="text-sm text-gray-600 font-medium">Select a Project</p>
        <p className="text-xs text-gray-400 mt-1">Click on a project to view SKU details</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-purple-600 font-bold">{project.quoteNumber}</span>
              {project.isRush && (
                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded">RUSH</span>
              )}
            </div>
            <h3 className="text-sm font-semibold text-gray-900 truncate mt-0.5">{project.projectName}</h3>
            {project.customerName && (
              <p className="text-xs text-gray-500 truncate">{project.customerName}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Quick stats */}
        <div className="flex items-center gap-4 mt-2 text-xs">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">SKUs:</span>
            <span className="font-semibold text-blue-600">{project.jobCount || jobs.length}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Total Qty:</span>
            <span className="font-semibold text-indigo-600">{project.totalComps || 0}</span>
          </div>
          {project.shipDate && (
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Ships:</span>
              <span className={`font-semibold ${isOverdue(project.shipDate) ? 'text-red-600' : 'text-gray-700'}`}>
                {formatShipDate(project.shipDate)}
              </span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 mt-3">
          {onViewDetail && (
            <button
              onClick={() => onViewDetail(project)}
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-medium flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Full Details
            </button>
          )}
          {!project.assignedTo && onPickUp && (
            <button
              onClick={() => onPickUp(project.id)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium"
            >
              Pick Up
            </button>
          )}
          {onNotifyPM && (
            <button
              onClick={() => onNotifyPM(project.id)}
              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded text-xs font-medium"
            >
              Notify PM
            </button>
          )}
          {onApproved && (
            <button
              onClick={() => onApproved(project.quoteNumber)}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
            >
              Mark Approved
            </button>
          )}
          {onSendToAssembly && (
            <button
              onClick={() => onSendToAssembly(project)}
              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium"
            >
              Send to Assembly
            </button>
          )}
        </div>
      </div>

      {/* SKU List */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-xs text-gray-500">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500" />
            Loading SKUs...
          </div>
        ) : jobs.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {jobs.map((job, i) => (
              <SKURow key={job.id || job.jobNumber || i} job={job} />
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-xs text-gray-500">
            <p>No SKU details available</p>
            <p className="text-gray-400 mt-1">Jobs will be loaded from JobBoss</p>
          </div>
        )}
      </div>

      {/* Footer with Artist info */}
      {project.assignedTo && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
          <span className="text-gray-500">Assigned to:</span>{' '}
          <span className="font-medium">{project.assignedTo}</span>
        </div>
      )}
    </div>
  );
}

function SKURow({ job }) {
  // Map skuClass to readable product type
  const getProductType = (skuClass) => {
    const types = {
      '2D': '2D Print',
      '3D': '3D Print',
      'COMBO': 'Combo',
      'SAMPLE': 'Sample',
      'KIT': 'Kit',
    };
    return types[skuClass] || skuClass || null;
  };

  const productType = getProductType(job.skuClass);

  return (
    <div className="px-4 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-purple-600 font-medium">{job.jobNumber}</span>
            {job.sku && job.sku !== job.jobNumber && (
              <span className="text-[10px] text-gray-400">({job.sku})</span>
            )}
            {productType && (
              <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded font-medium">
                {productType}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-800 truncate mt-0.5">{job.description || job.partDescription || 'No description'}</p>
          {/* Job Progress - Per-SKU canonical stage from Job Progress Contract */}
          {job.stage && (
            <div className="flex items-center gap-1.5 mt-1">
              <ProgressDots stage={job.stage} size="sm" />
              <span className="text-[10px] text-gray-500">{STAGE_LABELS[job.stage] || job.stage}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className="text-sm font-semibold text-indigo-600">{job.qty?.toLocaleString() || '—'}</span>
        </div>
      </div>
    </div>
  );
}
